import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Settings as SettingsIcon, Users, Zap, DollarSign, Save, CheckCircle, XCircle, Brain, Sparkles, Clock, Coins, RefreshCw, Phone, Volume2, MessageSquare, Shield, Globe, Check } from 'lucide-react';
import RBACSettings from './RBACSettings';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Settings = () => {
  const { user, token } = useAuth();
  const { currentLanguage, changeLanguage, supportedLanguages, t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [aiModelsLoading, setAiModelsLoading] = useState(false);
  const [aiModels, setAiModels] = useState([]);
  const [activeModel, setActiveModel] = useState('gpt-5.2');
  const [profileData, setProfileData] = useState({
    full_name: user?.full_name || '',
    company: user?.company || '',
    email: user?.email || ''
  });

  // Integration states
  const [integrationStatus, setIntegrationStatus] = useState({
    elevenlabs: { configured: false, status: 'not_configured' },
    twilio: { configured: false, status: 'not_configured', phone_number: null }
  });
  
  const [elevenLabsKey, setElevenLabsKey] = useState('');
  const [elevenLabsLoading, setElevenLabsLoading] = useState(false);
  const [elevenLabsVoices, setElevenLabsVoices] = useState([]);
  
  const [twilioCredentials, setTwilioCredentials] = useState({
    account_sid: '',
    auth_token: '',
    phone_number: ''
  });
  const [twilioLoading, setTwilioLoading] = useState(false);

  // Fetch AI models on component mount
  useEffect(() => {
    if (token) {
      fetchAiModels();
      fetchIntegrationStatus();
    }
  }, [token]);

  const fetchIntegrationStatus = async () => {
    try {
      const response = await axios.get(`${API}/integrations/status?token=${token}`);
      if (response.data.success) {
        setIntegrationStatus({
          elevenlabs: response.data.elevenlabs,
          twilio: response.data.twilio
        });
      }
    } catch (error) {
      console.error('Failed to fetch integration status:', error);
    }
  };

  const handleConfigureElevenLabs = async () => {
    if (!elevenLabsKey.trim()) {
      toast.error('Please enter your ElevenLabs API key');
      return;
    }
    
    setElevenLabsLoading(true);
    try {
      const response = await axios.post(`${API}/integrations/elevenlabs/configure?token=${token}`, {
        api_key: elevenLabsKey
      });
      
      if (response.data.success) {
        toast.success('ElevenLabs configured successfully!');
        setElevenLabsVoices(response.data.voices || []);
        setIntegrationStatus(prev => ({
          ...prev,
          elevenlabs: { configured: true, status: 'connected' }
        }));
        setElevenLabsKey(''); // Clear the input
      } else {
        toast.error(response.data.message || 'Configuration failed');
      }
    } catch (error) {
      toast.error('Failed to configure ElevenLabs');
    } finally {
      setElevenLabsLoading(false);
    }
  };

  const handleTestElevenLabs = async () => {
    setElevenLabsLoading(true);
    try {
      const response = await axios.post(`${API}/integrations/elevenlabs/test?token=${token}`, {
        api_key: elevenLabsKey || undefined
      });
      
      if (response.data.success) {
        toast.success(response.data.message);
        setElevenLabsVoices(response.data.voices || []);
      } else {
        toast.error(response.data.message || 'Test failed');
      }
    } catch (error) {
      toast.error('Connection test failed');
    } finally {
      setElevenLabsLoading(false);
    }
  };

  const handleConfigureTwilio = async () => {
    if (!twilioCredentials.account_sid || !twilioCredentials.auth_token || !twilioCredentials.phone_number) {
      toast.error('Please fill in all Twilio credentials');
      return;
    }
    
    setTwilioLoading(true);
    try {
      const response = await axios.post(`${API}/integrations/twilio/configure?token=${token}`, twilioCredentials);
      
      if (response.data.success) {
        toast.success('Twilio configured successfully!');
        setIntegrationStatus(prev => ({
          ...prev,
          twilio: { 
            configured: true, 
            status: 'connected',
            phone_number: twilioCredentials.phone_number
          }
        }));
        // Clear sensitive fields
        setTwilioCredentials({ account_sid: '', auth_token: '', phone_number: '' });
      } else {
        toast.error(response.data.message || 'Configuration failed');
      }
    } catch (error) {
      toast.error('Failed to configure Twilio');
    } finally {
      setTwilioLoading(false);
    }
  };

  const handleTestTwilio = async () => {
    setTwilioLoading(true);
    try {
      const response = await axios.post(`${API}/integrations/twilio/test?token=${token}`, 
        twilioCredentials.account_sid ? twilioCredentials : undefined
      );
      
      if (response.data.success) {
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message || 'Test failed');
      }
    } catch (error) {
      toast.error('Connection test failed');
    } finally {
      setTwilioLoading(false);
    }
  };

  const fetchAiModels = async () => {
    setAiModelsLoading(true);
    try {
      const response = await axios.get(`${API}/ai/models?token=${token}`);
      if (response.data.success) {
        setAiModels(response.data.models);
        setActiveModel(response.data.active_model);
      }
    } catch (error) {
      console.error('Failed to fetch AI models:', error);
      // Use default models if API fails
      setAiModels([
        {
          model_id: 'gpt-5.2',
          name: 'GPT-5.2',
          provider: 'openai',
          is_active: true,
          coding_score: 85,
          reasoning_score: 92,
          creative_score: 88,
          cost_per_1m_output: 15.0,
          avg_latency_ms: 800,
          supports_vision: true,
          supports_voice: true,
          tags: ['general', 'proven', 'fast']
        },
        {
          model_id: 'claude-sonnet-4.5',
          name: 'Claude Sonnet 4.5',
          provider: 'anthropic',
          is_active: true,
          coding_score: 88,
          reasoning_score: 95,
          creative_score: 92,
          cost_per_1m_output: 15.0,
          avg_latency_ms: 1200,
          supports_vision: true,
          supports_voice: false,
          tags: ['reasoning', 'writing', 'premium']
        },
        {
          model_id: 'glm-5',
          name: 'GLM-5 (Z.ai)',
          provider: 'zai',
          is_active: true,
          coding_score: 92,
          reasoning_score: 85,
          creative_score: 80,
          cost_per_1m_output: 2.55,
          avg_latency_ms: 900,
          supports_vision: false,
          supports_voice: false,
          tags: ['coding', 'long-context', 'budget']
        },
        {
          model_id: 'gemini-3-pro',
          name: 'Gemini 3 Pro',
          provider: 'google',
          is_active: true,
          coding_score: 80,
          reasoning_score: 88,
          creative_score: 85,
          cost_per_1m_output: 5.0,
          avg_latency_ms: 700,
          supports_vision: true,
          supports_voice: false,
          tags: ['fast', 'multimodal', 'budget']
        }
      ]);
    } finally {
      setAiModelsLoading(false);
    }
  };

  const handleSelectModel = async (modelId) => {
    // Check if user is admin
    if (user?.role !== 'admin') {
      toast.error('Only company admins can change AI model settings');
      return;
    }
    
    try {
      const response = await axios.post(`${API}/ai/models/select?token=${token}`, {
        model_id: modelId
      });
      
      if (response.data.success) {
        setActiveModel(modelId);
        const model = aiModels.find(m => m.model_id === modelId);
        toast.success(`Company AI model switched to ${model?.name || modelId}`);
      }
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Only company admins can change AI model settings');
      } else {
        toast.error('Failed to switch AI model');
      }
    }
  };

  const [testingModel, setTestingModel] = useState(null);
  
  const handleTestConnection = async (modelId, e) => {
    e.stopPropagation(); // Prevent card click
    setTestingModel(modelId);
    
    try {
      const response = await axios.post(`${API}/ai/models/${modelId}/test?token=${token}`);
      
      if (response.data.success) {
        toast.success(`${response.data.message} (${response.data.latency_ms}ms)`);
      } else {
        toast.error(response.data.message || 'Connection test failed');
      }
    } catch (error) {
      toast.error('Failed to test model connection');
    } finally {
      setTestingModel(null);
    }
  };

  const isAdmin = user?.role === 'admin';

  const getProviderColor = (provider) => {
    const colors = {
      openai: 'bg-emerald-500',
      anthropic: 'bg-orange-500',
      google: 'bg-blue-500',
      zai: 'bg-purple-500',
      deepseek: 'bg-cyan-500',
      mistral: 'bg-red-500'
    };
    return colors[provider] || 'bg-slate-500';
  };

  const getScoreColor = (score) => {
    if (score >= 90) return 'text-success';
    if (score >= 80) return 'text-warning';
    return 'text-slate-500';
  };

  const integrations = [
    {
      name: 'Twilio',
      description: 'AI-powered voice calling',
      status: 'ready',
      icon: '📞',
      docs: 'https://www.twilio.com/docs',
      configured: false
    },
    {
      name: 'ElevenLabs',
      description: 'Natural AI voice generation',
      status: 'ready',
      icon: '🎙️',
      docs: 'https://elevenlabs.io/docs',
      configured: false
    },
    {
      name: 'SendGrid',
      description: 'Email campaign automation',
      status: 'ready',
      icon: '📧',
      docs: 'https://docs.sendgrid.com',
      configured: false
    },
    {
      name: 'OpenAI GPT-5.2',
      description: 'AI assistance and content generation',
      status: 'active',
      icon: '🤖',
      docs: 'https://platform.openai.com/docs',
      configured: true
    },
    {
      name: 'Gemini Nano Banana',
      description: 'AI image generation',
      status: 'active',
      icon: '🎨',
      docs: 'https://ai.google.dev/gemini-api/docs',
      configured: true
    },
    {
      name: 'Google Ads',
      description: 'Campaign tracking & management',
      status: 'coming_soon',
      icon: '📊',
      docs: 'https://developers.google.com/google-ads/api',
      configured: false
    },
    {
      name: 'Meta Ads',
      description: 'Facebook & Instagram advertising',
      status: 'coming_soon',
      icon: '📱',
      docs: 'https://developers.facebook.com/docs/marketing-apis',
      configured: false
    }
  ];

  const roles = [
    {
      name: 'Admin',
      description: 'Full access to all features, settings, and team management',
      permissions: ['read', 'write', 'delete', 'manage_team', 'billing'],
      color: 'bg-error'
    },
    {
      name: 'Manager',
      description: 'Access to team data, reports, and pipeline management',
      permissions: ['read', 'write', 'view_reports', 'manage_pipeline'],
      color: 'bg-warning'
    },
    {
      name: 'Sales Rep',
      description: 'Access to own deals, contacts, and calling features',
      permissions: ['read', 'write', 'make_calls'],
      color: 'bg-info'
    },
    {
      name: 'Viewer',
      description: 'Read-only access to dashboards and reports',
      permissions: ['read', 'view_reports'],
      color: 'bg-slate-400'
    }
  ];

  const handleSaveProfile = async () => {
    setLoading(true);
    try {
      // In a real implementation, this would call an API to update user profile
      toast.success('Profile updated successfully!');
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    if (status === 'active') {
      return <Badge className="bg-success text-white"><CheckCircle size={12} className="mr-1" />Active</Badge>;
    }
    if (status === 'ready') {
      return <Badge className="bg-warning text-white">Ready to Configure</Badge>;
    }
    return <Badge className="bg-slate-400 text-white">Coming Soon</Badge>;
  };

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <h3 className="text-2xl font-heading font-bold text-primary">Settings</h3>
        <p className="text-sm text-slate-500 mt-1">Manage your CRM configuration and preferences</p>
      </div>

      <Tabs defaultValue="general" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="general">General</TabsTrigger>
          <TabsTrigger value="team">Team & Roles</TabsTrigger>
          <TabsTrigger value="integrations">Integrations</TabsTrigger>
          <TabsTrigger value="billing">Billing</TabsTrigger>
        </TabsList>

        <TabsContent value="general" className="space-y-4 mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <SettingsIcon size={20} />
                Profile Settings
              </CardTitle>
              <CardDescription>Update your personal information and preferences</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="full_name">Full Name</Label>
                  <Input
                    id="full_name"
                    value={profileData.full_name}
                    onChange={(e) => setProfileData({ ...profileData, full_name: e.target.value })}
                    placeholder="John Doe"
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="company">Company</Label>
                  <Input
                    id="company"
                    value={profileData.company}
                    onChange={(e) => setProfileData({ ...profileData, company: e.target.value })}
                    placeholder="Your Company Inc."
                    className="mt-1"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={profileData.email}
                  disabled
                  className="mt-1 bg-slate-50"
                />
                <p className="text-xs text-slate-500 mt-1">Email cannot be changed</p>
              </div>

              <div className="pt-4">
                <Button 
                  onClick={handleSaveProfile}
                  disabled={loading}
                  className="bg-primary"
                >
                  <Save size={16} className="mr-2" />
                  {loading ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Preferences</CardTitle>
              <CardDescription>Customize your CRM experience</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between p-3 border rounded-lg">
                <div>
                  <p className="font-medium text-sm">Email Notifications</p>
                  <p className="text-xs text-slate-500">Receive updates about your deals and tasks</p>
                </div>
                <Badge className="bg-success text-white">Enabled</Badge>
              </div>

              <div className="flex items-center justify-between p-3 border rounded-lg">
                <div>
                  <p className="font-medium text-sm">AI Assistant Auto-launch</p>
                  <p className="text-xs text-slate-500">Show AI assistant when you login</p>
                </div>
                <Badge className="bg-success text-white">Enabled</Badge>
              </div>

              <div className="flex items-center justify-between p-3 border rounded-lg">
                <div>
                  <p className="font-medium text-sm">Desktop Notifications</p>
                  <p className="text-xs text-slate-500">Browser notifications for important events</p>
                </div>
                <Badge className="bg-slate-400 text-white">Disabled</Badge>
              </div>
            </CardContent>
          </Card>

          {/* Language Settings Card */}
          <Card data-testid="language-settings-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe size={20} />
                {t('settings.language', 'Language')}
              </CardTitle>
              <CardDescription>{t('settings.languageDescription', 'Choose your preferred language for the interface')}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {supportedLanguages.map((lang) => {
                  const flags = {
                    en: '🇺🇸',
                    ar: '🇸🇦',
                    es: '🇪🇸',
                    fr: '🇫🇷',
                    hi: '🇮🇳',
                    de: '🇩🇪',
                    zh: '🇨🇳',
                  };
                  const isSelected = currentLanguage === lang.code;
                  
                  return (
                    <button
                      key={lang.code}
                      onClick={() => {
                        changeLanguage(lang.code);
                        toast.success(t('settings.changesSaved', 'Language preference saved!'));
                      }}
                      className={`flex items-center gap-3 p-4 rounded-lg border-2 transition-all ${
                        isSelected 
                          ? 'border-primary bg-primary/5' 
                          : 'border-slate-200 hover:border-primary/50 hover:bg-slate-50'
                      }`}
                      data-testid={`lang-select-${lang.code}`}
                    >
                      <span className="text-2xl">{flags[lang.code]}</span>
                      <div className="text-left flex-1">
                        <p className="font-medium text-sm">{lang.name}</p>
                        {lang.dir === 'rtl' && (
                          <p className="text-xs text-slate-500">RTL</p>
                        )}
                      </div>
                      {isSelected && (
                        <Check size={18} className="text-primary" />
                      )}
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-slate-500 mt-4">
                Your language preference will be saved to your profile and applied across all sessions.
              </p>
            </CardContent>
          </Card>

          {/* Role & Access Card */}
          <Card data-testid="role-access-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield size={20} />
                Role & Access
              </CardTitle>
              <CardDescription>Your current role and platform access level</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 border rounded-lg bg-slate-50">
                <div>
                  <p className="font-medium text-sm">Current Role</p>
                  <p className="text-xs text-slate-500 mt-1">Your access level in the platform</p>
                </div>
                <Badge 
                  className={`text-white ${
                    user?.role === 'super_admin' 
                      ? 'bg-purple-600' 
                      : user?.role === 'admin' 
                        ? 'bg-blue-600' 
                        : 'bg-slate-500'
                  }`}
                >
                  {user?.role === 'super_admin' ? 'Super Admin' : user?.role || 'User'}
                </Badge>
              </div>
              
              {user?.role !== 'super_admin' && (
                <div className="border rounded-lg p-4 bg-purple-50 border-purple-200">
                  <div className="flex items-start gap-3">
                    <Shield className="text-purple-600 mt-0.5" size={20} />
                    <div className="flex-1">
                      <p className="font-medium text-sm text-purple-800">Become Super Admin</p>
                      <p className="text-xs text-purple-600 mt-1">
                        Super Admins can manage tenant accounts, subscriptions, and platform-wide settings.
                        This is required to access the Account Management module.
                      </p>
                      <Button 
                        className="mt-3 bg-purple-600 hover:bg-purple-700"
                        size="sm"
                        onClick={async () => {
                          try {
                            const response = await axios.post(`${API}/auth/promote-super-admin?token=${token}`, {
                              email: user.email
                            });
                            if (response.data.success) {
                              toast.success(response.data.message);
                              toast.info(response.data.note);
                            }
                          } catch (error) {
                            toast.error(error.response?.data?.detail || 'Failed to promote to super admin');
                          }
                        }}
                        data-testid="promote-super-admin-btn"
                      >
                        <Shield size={14} className="mr-2" />
                        Upgrade to Super Admin
                      </Button>
                    </div>
                  </div>
                </div>
              )}
              
              {user?.role === 'super_admin' && (
                <div className="border rounded-lg p-4 bg-green-50 border-green-200">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="text-green-600" size={20} />
                    <div>
                      <p className="font-medium text-sm text-green-800">Full Platform Access</p>
                      <p className="text-xs text-green-600 mt-1">
                        You have access to Account Management and all administrative features.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="team" className="space-y-4 mt-6">
          {/* Team Management Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users size={20} />
                Team Management
              </CardTitle>
              <CardDescription>Manage team members and assign roles</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="mb-6">
                <p className="text-sm text-slate-600 mb-4">Current team size: <strong>1 member</strong></p>
                <Button className="bg-primary">
                  <Users size={16} className="mr-2" />
                  Invite Team Member
                </Button>
              </div>

              <div className="space-y-3 mt-6">
                <h4 className="font-semibold text-sm text-slate-700 mb-3">Available Roles</h4>
                {roles.map((role, index) => (
                  <div key={index} className="border rounded-lg p-4 hover:border-primary/50 transition-colors">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h5 className="font-semibold text-primary flex items-center gap-2">
                          {role.name}
                          <Badge className={`${role.color} text-white text-xs`}>
                            {role.permissions.length} permissions
                          </Badge>
                        </h5>
                        <p className="text-sm text-slate-600 mt-1">{role.description}</p>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-1 mt-3">
                      {role.permissions.map((perm, pidx) => (
                        <Badge key={pidx} variant="secondary" className="text-xs">
                          {perm.replace('_', ' ')}
                        </Badge>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* RBAC Settings */}
          <RBACSettings />
        </TabsContent>

        <TabsContent value="integrations" className="space-y-4 mt-6">
          {/* AI Model Switcher Section */}
          <Card data-testid="ai-model-switcher">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Brain size={20} className="text-accent" />
                AI Model Manager
              </CardTitle>
              <CardDescription>Select which AI model powers your CRM's intelligence features</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Sparkles size={16} className="text-accent" />
                  <span className="text-sm font-medium">Current Model:</span>
                  <Badge className="bg-accent text-white">
                    {aiModels.find(m => m.model_id === activeModel)?.name || activeModel}
                  </Badge>
                </div>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={fetchAiModels}
                  disabled={aiModelsLoading}
                  data-testid="refresh-models-btn"
                >
                  <RefreshCw size={14} className={`mr-1 ${aiModelsLoading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>

              {aiModelsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw size={24} className="animate-spin text-accent" />
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {aiModels.filter(m => m.is_active).map((model) => (
                    <div 
                      key={model.model_id}
                      data-testid={`model-card-${model.model_id}`}
                      className={`border-2 rounded-lg p-4 transition-all duration-200 ${
                        activeModel === model.model_id 
                          ? 'border-accent bg-accent/5 shadow-md' 
                          : 'border-slate-200 hover:border-accent/50 hover:bg-slate-50'
                      } ${isAdmin ? 'cursor-pointer' : 'cursor-default'}`}
                      onClick={() => isAdmin && handleSelectModel(model.model_id)}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <div className={`w-3 h-3 rounded-full ${getProviderColor(model.provider)}`} />
                          <div>
                            <h5 className="font-semibold text-primary">{model.name}</h5>
                            <p className="text-xs text-slate-500 capitalize">{model.provider}</p>
                          </div>
                        </div>
                        {activeModel === model.model_id && (
                          <Badge className="bg-success text-white" data-testid={`active-badge-${model.model_id}`}>
                            <CheckCircle size={12} className="mr-1" />
                            Active
                          </Badge>
                        )}
                      </div>

                      {/* Model Stats */}
                      <div className="grid grid-cols-3 gap-2 mb-3">
                        <div className="text-center p-2 bg-slate-50 rounded">
                          <p className="text-xs text-slate-500">Coding</p>
                          <p className={`font-bold ${getScoreColor(model.coding_score)}`}>
                            {model.coding_score || '-'}
                          </p>
                        </div>
                        <div className="text-center p-2 bg-slate-50 rounded">
                          <p className="text-xs text-slate-500">Reasoning</p>
                          <p className={`font-bold ${getScoreColor(model.reasoning_score)}`}>
                            {model.reasoning_score || '-'}
                          </p>
                        </div>
                        <div className="text-center p-2 bg-slate-50 rounded">
                          <p className="text-xs text-slate-500">Creative</p>
                          <p className={`font-bold ${getScoreColor(model.creative_score)}`}>
                            {model.creative_score || '-'}
                          </p>
                        </div>
                      </div>

                      {/* Capabilities */}
                      <div className="flex flex-wrap gap-1 mb-3">
                        {model.supports_vision && (
                          <Badge variant="secondary" className="text-xs">Vision</Badge>
                        )}
                        {model.supports_voice && (
                          <Badge variant="secondary" className="text-xs">Voice</Badge>
                        )}
                        {model.tags?.map((tag, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs">{tag}</Badge>
                        ))}
                      </div>

                      {/* Cost & Latency */}
                      <div className="flex items-center justify-between text-xs text-slate-500 pt-2 border-t">
                        <div className="flex items-center gap-1">
                          <Coins size={12} />
                          <span>${model.cost_per_1m_output}/1M tokens</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Clock size={12} />
                          <span>{model.avg_latency_ms}ms avg</span>
                        </div>
                      </div>
                      
                      {/* Test Connection Button */}
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full mt-3"
                        onClick={(e) => handleTestConnection(model.model_id, e)}
                        disabled={testingModel === model.model_id}
                        data-testid={`test-connection-${model.model_id}`}
                      >
                        {testingModel === model.model_id ? (
                          <>
                            <RefreshCw size={14} className="mr-1 animate-spin" />
                            Testing...
                          </>
                        ) : (
                          <>
                            <Zap size={14} className="mr-1" />
                            Test Connection
                          </>
                        )}
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              {!isAdmin && (
                <div className="mt-4 bg-warning/10 border border-warning/30 rounded-lg p-3">
                  <p className="text-sm text-warning font-medium">Admin Access Required</p>
                  <p className="text-xs text-slate-600">
                    Only company admins can change the active AI model. Contact your admin to request changes.
                  </p>
                </div>
              )}

              <div className="mt-4 bg-info/5 border border-info/20 rounded-lg p-4">
                <p className="text-sm text-info font-medium mb-1">How Model Switching Works</p>
                <p className="text-xs text-slate-600">
                  {isAdmin ? (
                    <>When you select a model, all AI-powered features for your company will use that model. 
                    Click "Test Connection" to verify a model is working before switching.</>
                  ) : (
                    <>Your company currently uses {aiModels.find(m => m.model_id === activeModel)?.name || activeModel} for all AI features.</>
                  )}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Voice & Calling Integrations */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Phone size={20} />
                Voice & Calling Integrations
              </CardTitle>
              <CardDescription>Configure ElevenLabs for AI voice and Twilio for calling/SMS</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* ElevenLabs Configuration */}
              <div className="border-2 rounded-lg p-5" data-testid="elevenlabs-config">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-violet-100 rounded-lg">
                      <Volume2 size={24} className="text-violet-600" />
                    </div>
                    <div>
                      <h5 className="font-semibold text-primary">ElevenLabs</h5>
                      <p className="text-xs text-slate-500">AI text-to-speech and voice synthesis</p>
                    </div>
                  </div>
                  {integrationStatus.elevenlabs.configured ? (
                    <Badge className="bg-success text-white"><CheckCircle size={12} className="mr-1" />Connected</Badge>
                  ) : (
                    <Badge className="bg-slate-400 text-white">Not Configured</Badge>
                  )}
                </div>

                <div className="space-y-3">
                  <div>
                    <Label htmlFor="elevenlabs-key">API Key</Label>
                    <Input
                      id="elevenlabs-key"
                      type="password"
                      value={elevenLabsKey}
                      onChange={(e) => setElevenLabsKey(e.target.value)}
                      placeholder={integrationStatus.elevenlabs.configured ? '••••••••••••••••' : 'Enter your ElevenLabs API key'}
                      className="mt-1"
                      data-testid="elevenlabs-key-input"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Get your API key from <a href="https://elevenlabs.io/app/settings/api-keys" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">ElevenLabs Dashboard</a>
                    </p>
                  </div>

                  <div className="flex gap-2">
                    <Button 
                      onClick={handleTestElevenLabs}
                      variant="outline"
                      disabled={elevenLabsLoading}
                      data-testid="elevenlabs-test-btn"
                    >
                      {elevenLabsLoading ? <RefreshCw size={14} className="mr-1 animate-spin" /> : <Zap size={14} className="mr-1" />}
                      Test Connection
                    </Button>
                    <Button 
                      onClick={handleConfigureElevenLabs}
                      disabled={elevenLabsLoading || !elevenLabsKey}
                      data-testid="elevenlabs-save-btn"
                    >
                      <Save size={14} className="mr-1" />
                      Save & Connect
                    </Button>
                  </div>

                  {elevenLabsVoices.length > 0 && (
                    <div className="mt-3 p-3 bg-violet-50 rounded-lg">
                      <p className="text-xs font-medium text-violet-700 mb-2">Available Voices:</p>
                      <div className="flex flex-wrap gap-1">
                        {elevenLabsVoices.slice(0, 5).map((voice) => (
                          <Badge key={voice.id} variant="outline" className="text-xs">{voice.name}</Badge>
                        ))}
                        {elevenLabsVoices.length > 5 && (
                          <Badge variant="outline" className="text-xs">+{elevenLabsVoices.length - 5} more</Badge>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Twilio Configuration */}
              <div className="border-2 rounded-lg p-5" data-testid="twilio-config">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-red-100 rounded-lg">
                      <Phone size={24} className="text-red-600" />
                    </div>
                    <div>
                      <h5 className="font-semibold text-primary">Twilio</h5>
                      <p className="text-xs text-slate-500">VoIP calling, SMS, and phone features</p>
                    </div>
                  </div>
                  {integrationStatus.twilio.configured ? (
                    <div className="text-right">
                      <Badge className="bg-success text-white"><CheckCircle size={12} className="mr-1" />Connected</Badge>
                      {integrationStatus.twilio.phone_number && (
                        <p className="text-xs text-slate-500 mt-1">{integrationStatus.twilio.phone_number}</p>
                      )}
                    </div>
                  ) : (
                    <Badge className="bg-slate-400 text-white">Not Configured</Badge>
                  )}
                </div>

                <div className="space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="twilio-sid">Account SID</Label>
                      <Input
                        id="twilio-sid"
                        value={twilioCredentials.account_sid}
                        onChange={(e) => setTwilioCredentials({ ...twilioCredentials, account_sid: e.target.value })}
                        placeholder={integrationStatus.twilio.configured ? '••••••••••••' : 'ACxxxxxxxxxxxxxxx'}
                        className="mt-1"
                        data-testid="twilio-sid-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="twilio-token">Auth Token</Label>
                      <Input
                        id="twilio-token"
                        type="password"
                        value={twilioCredentials.auth_token}
                        onChange={(e) => setTwilioCredentials({ ...twilioCredentials, auth_token: e.target.value })}
                        placeholder={integrationStatus.twilio.configured ? '••••••••••••' : 'Your auth token'}
                        className="mt-1"
                        data-testid="twilio-token-input"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="twilio-phone">Phone Number</Label>
                    <Input
                      id="twilio-phone"
                      value={twilioCredentials.phone_number}
                      onChange={(e) => setTwilioCredentials({ ...twilioCredentials, phone_number: e.target.value })}
                      placeholder={integrationStatus.twilio.configured ? integrationStatus.twilio.phone_number : '+1234567890'}
                      className="mt-1"
                      data-testid="twilio-phone-input"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Get credentials from <a href="https://console.twilio.com" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">Twilio Console</a>
                    </p>
                  </div>

                  <div className="flex gap-2">
                    <Button 
                      onClick={handleTestTwilio}
                      variant="outline"
                      disabled={twilioLoading}
                      data-testid="twilio-test-btn"
                    >
                      {twilioLoading ? <RefreshCw size={14} className="mr-1 animate-spin" /> : <Zap size={14} className="mr-1" />}
                      Test Connection
                    </Button>
                    <Button 
                      onClick={handleConfigureTwilio}
                      disabled={twilioLoading || !twilioCredentials.account_sid || !twilioCredentials.auth_token || !twilioCredentials.phone_number}
                      data-testid="twilio-save-btn"
                    >
                      <Save size={14} className="mr-1" />
                      Save & Connect
                    </Button>
                  </div>

                  {integrationStatus.twilio.configured && (
                    <div className="mt-3 p-3 bg-green-50 rounded-lg flex items-center gap-2">
                      <CheckCircle size={16} className="text-green-600" />
                      <p className="text-xs text-green-700">
                        Twilio is configured. You can now make calls and send SMS from the app.
                      </p>
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-info/5 border border-info/20 rounded-lg p-4">
                <p className="text-sm text-info font-medium mb-1">🔐 Security Note</p>
                <p className="text-xs text-slate-600">
                  Your API keys are stored securely and encrypted. They are never exposed in the frontend and are only used server-side.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Existing Integrations Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Zap size={20} />
                Integrations
              </CardTitle>
              <CardDescription>Connect your favorite tools and services</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {integrations.map((integration, index) => (
                  <div 
                    key={index}
                    className="border-2 rounded-lg p-4 hover:border-accent/30 transition-colors"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <span className="text-3xl">{integration.icon}</span>
                        <div>
                          <h5 className="font-semibold text-primary">{integration.name}</h5>
                          <p className="text-xs text-slate-500">{integration.description}</p>
                        </div>
                      </div>
                      {getStatusBadge(integration.status)}
                    </div>

                    {integration.status === 'active' ? (
                      <div className="bg-success/5 border border-success/20 rounded p-2 text-xs text-success">
                        ✓ Connected and working
                      </div>
                    ) : integration.status === 'ready' ? (
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="w-full mt-2"
                        onClick={() => toast.info(`Configure ${integration.name} in backend/.env file`)}
                      >
                        Configure
                      </Button>
                    ) : (
                      <p className="text-xs text-slate-400 mt-2">Available in future updates</p>
                    )}

                    <a 
                      href={integration.docs}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-accent hover:underline block mt-2"
                    >
                      View Documentation →
                    </a>
                  </div>
                ))}
              </div>

              <div className="mt-6 bg-info/5 border border-info/20 rounded-lg p-4">
                <p className="text-sm text-info font-medium mb-2">💡 Using Emergent Universal Key</p>
                <p className="text-xs text-slate-600">
                  Your AI features (OpenAI, Gemini) are powered by the Emergent Universal Key. 
                  To add your own API keys or configure other integrations, update the backend/.env file.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="billing" className="space-y-4 mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign size={20} />
                Billing & Subscription
              </CardTitle>
              <CardDescription>Manage your subscription and payment methods</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="border-2 border-accent rounded-lg p-6 bg-accent/5">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h4 className="text-xl font-bold text-primary">Enterprise Plan</h4>
                      <p className="text-sm text-slate-600">All features included</p>
                    </div>
                    <Badge className="bg-accent text-white text-lg px-4 py-2">Active</Badge>
                  </div>

                  <div className="space-y-2 mb-4">
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle size={16} className="text-success" />
                      <span>Unlimited contacts and deals</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle size={16} className="text-success" />
                      <span>AI Assistant & Campaign Creator</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle size={16} className="text-success" />
                      <span>Revenue Forecasting & Analytics</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle size={16} className="text-success" />
                      <span>AI Calling & Voice Intelligence</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle size={16} className="text-success" />
                      <span>Workflow Automation</span>
                    </div>
                  </div>
                </div>

                <div className="border rounded-lg p-4">
                  <h5 className="font-semibold text-sm mb-3">Payment Method</h5>
                  <div className="flex items-center gap-3 p-3 bg-slate-50 rounded">
                    <div className="w-12 h-8 bg-slate-200 rounded flex items-center justify-center">
                      💳
                    </div>
                    <div>
                      <p className="text-sm font-medium">Visa ending in 4242</p>
                      <p className="text-xs text-slate-500">Expires 12/2025</p>
                    </div>
                  </div>
                  <Button variant="outline" size="sm" className="mt-3">
                    Update Payment Method
                  </Button>
                </div>

                <div className="border rounded-lg p-4">
                  <h5 className="font-semibold text-sm mb-3">Billing History</h5>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between p-2 hover:bg-slate-50 rounded">
                      <div>
                        <p className="text-sm font-medium">Feb 2026</p>
                        <p className="text-xs text-slate-500">Paid on Feb 1, 2026</p>
                      </div>
                      <span className="text-sm font-semibold">$299.00</span>
                    </div>
                    <div className="flex items-center justify-between p-2 hover:bg-slate-50 rounded">
                      <div>
                        <p className="text-sm font-medium">Jan 2026</p>
                        <p className="text-xs text-slate-500">Paid on Jan 1, 2026</p>
                      </div>
                      <span className="text-sm font-semibold">$299.00</span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Settings;
